import { useCallback, useEffect, useRef } from 'react'

import { parseScript, type Script } from '@/api/studio-production'
import { parseWriterScript } from '@/api/studio-writer'

/** 保存成功后 debounce 调用拆场 API，同步左侧场次树 */
export function useScriptAutoParse(opts: {
  enabled: boolean
  scriptId: number
  productionId?: string
  writer?: boolean
  onParsed: (episodeNo: number, data: Script) => void
  onError?: (episodeNo: number, err: unknown) => void
  delayMs?: number
}) {
  const { enabled, productionId, scriptId, writer = false, onParsed, onError, delayMs = 700 } = opts
  const pendingRef = useRef<Set<number>>(new Set())
  const timerRef = useRef<number | null>(null)
  const onParsedRef = useRef(onParsed)
  const onErrorRef = useRef(onError)
  onParsedRef.current = onParsed
  onErrorRef.current = onError

  const flush = useCallback(async () => {
    if (!enabled || pendingRef.current.size === 0) return
    const eps = [...pendingRef.current]
    pendingRef.current.clear()
    for (const epNo of eps) {
      try {
        const data = writer
          ? await parseWriterScript(scriptId, epNo)
          : await parseScript(productionId!, scriptId, epNo)
        onParsedRef.current(epNo, data)
      } catch (err) {
        onErrorRef.current?.(epNo, err)
      }
    }
  }, [enabled, productionId, scriptId, writer])

  const scheduleParse = useCallback(
    (episodeNo: number) => {
      if (!enabled) return
      pendingRef.current.add(episodeNo)
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        void flush()
      }, delayMs)
    },
    [enabled, delayMs, flush],
  )

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current)
    },
    [],
  )

  return { scheduleParse, flushParse: flush }
}
